import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { normalizePlan as normalizeSharedPlan, resolveEntitlements } from "@lucralink/shared";
import type {
  Plan,
  SetTenantStorefrontInput,
  StorefrontBranding,
  StorefrontItem,
  StorefrontStatus,
  UpsertStorefrontItemInput,
} from "@lucralink/shared";

// -------------------------------------------------------------------------
// Hooks da curadoria da vitrine (apps/panel).
//
// Leitura: tenant (slug/branding/status/plan) via tabela tenants e itens via
// storefront_items (ambos protegidos por RLS multi-tenant).
// Escrita: RPCs SECURITY DEFINER (set_tenant_storefront, upsert_storefront_item,
// reorder_storefront_items). Nenhuma rota gera link novo: o item so referencia
// um public.links ja creditavel (gerador oficial).
// -------------------------------------------------------------------------

export const STOREFRONT_QUERY_KEY = ["storefront"] as const;
export const STOREFRONT_ITEMS_QUERY_KEY = ["storefront", "items"] as const;

/** Estado da vitrine do tenant logado, montado para a UI de curadoria.
 *  `isPro` ja resolve `plan` + `pro_until` (resolveEntitlements) - usar
 *  SEMPRE este campo pro gate de UI em vez de comparar `plan !== "free"`
 *  cru, que ignora assinatura vencida (mesma classe de bug do Codex em
 *  GeminiCredentialSection.tsx, replicada aqui pelo reuso deste hook em
 *  BioPage/MeuSitePage/VitrinePage). */
export interface TenantStorefront {
  tenantId: string;
  slug: string | null;
  branding: StorefrontBranding;
  status: StorefrontStatus;
  plan: Plan;
  proUntil: string | null;
  isPro: boolean;
  /** Link na bio liberado (entitlements.hasBioLink). */
  hasBioLink: boolean;
}

interface TenantStorefrontRow {
  id: string;
  slug: string | null;
  branding: StorefrontBranding | null;
  storefront_status: string;
  plan: string;
  pro_until: string | null;
}

// O PostgREST devolve o relacionamento embutido (links) ora como objeto,
// ora como array de um elemento, dependendo da inferencia. Tratamos ambos.
type EmbeddedLink = { marketplace_code: string; destination_url: string };

interface StorefrontItemRow {
  id: string;
  link_id: string;
  title: string;
  image_url: string | null;
  price_cents: number | null;
  position: number;
  is_visible: boolean;
  links: EmbeddedLink | EmbeddedLink[] | null;
}

function pickEmbeddedLink(links: StorefrontItemRow["links"]): EmbeddedLink | null {
  if (!links) return null;
  return Array.isArray(links) ? (links[0] ?? null) : links;
}

function normalizeStatus(value: string): StorefrontStatus {
  return value === "published" ? "published" : "draft";
}

// Reusa @lucralink/shared::normalizePlan (fonte unica de verdade do gating por
// plano). Bug corrigido: a normalizacao local antiga so reconhecia "pro",
// colapsando tenants "pro_ai" para "free" e mascarando no client o fix do
// migration 20260714130000 (set_tenant_storefront ja aceita pro_ai no banco).
function mapTenantRow(row: TenantStorefrontRow): TenantStorefront {
  const plan = normalizeSharedPlan(row.plan);
  return {
    tenantId: row.id,
    slug: row.slug,
    branding: row.branding ?? {},
    status: normalizeStatus(row.storefront_status),
    plan,
    proUntil: row.pro_until ?? null,
    isPro: resolveEntitlements(plan, row.pro_until ?? null).hasShowcase,
    hasBioLink: resolveEntitlements(plan, row.pro_until ?? null).hasBioLink,
  };
}

function mapItemRow(row: StorefrontItemRow): StorefrontItem {
  const link = pickEmbeddedLink(row.links);
  return {
    id: row.id,
    linkId: row.link_id,
    marketplaceCode: link?.marketplace_code ?? "",
    destinationUrl: link?.destination_url ?? "",
    title: row.title,
    imageUrl: row.image_url,
    priceCents: row.price_cents,
    position: row.position,
    isVisible: row.is_visible,
  };
}

async function fetchTenantStorefront(): Promise<TenantStorefront | null> {
  const { data, error } = await supabase
    .from("tenants")
    .select("id, slug, branding, storefront_status, plan, pro_until")
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return mapTenantRow(data as TenantStorefrontRow);
}

async function fetchStorefrontItems(): Promise<StorefrontItem[]> {
  const { data, error } = await supabase
    .from("storefront_items")
    .select(
      "id, link_id, title, image_url, price_cents, position, is_visible, links(marketplace_code, destination_url)",
    )
    .order("position", { ascending: true });

  if (error) throw error;

  return ((data ?? []) as unknown as StorefrontItemRow[]).map(mapItemRow);
}

/** Le slug/branding/status/plan do tenant logado. */
export function useStorefront() {
  return useQuery<TenantStorefront | null, Error>({
    queryKey: STOREFRONT_QUERY_KEY,
    queryFn: fetchTenantStorefront,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Le itens curados do tenant logado, ordenados por position. */
export function useStorefrontItems() {
  return useQuery<StorefrontItem[], Error>({
    queryKey: STOREFRONT_ITEMS_QUERY_KEY,
    queryFn: fetchStorefrontItems,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

async function callSetTenantStorefront(
  input: SetTenantStorefrontInput,
): Promise<TenantStorefront> {
  const { data, error } = await supabase.rpc("set_tenant_storefront", {
    p_slug: input.slug,
    p_branding: input.branding,
    p_status: input.status,
  });

  if (error) throw error;
  if (!data) throw new Error("storefront_update_failed");

  return mapTenantRow(data as TenantStorefrontRow);
}

/** Define slug/branding/status. Publish exige plano pro (gate no DB). */
export function useSetTenantStorefront() {
  const queryClient = useQueryClient();

  return useMutation<TenantStorefront, Error, SetTenantStorefrontInput>({
    mutationFn: callSetTenantStorefront,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STOREFRONT_QUERY_KEY });
    },
  });
}

async function callUpsertStorefrontItem(
  input: UpsertStorefrontItemInput,
): Promise<StorefrontItem> {
  const { data, error } = await supabase.rpc("upsert_storefront_item", {
    p_item_id: input.itemId ?? null,
    p_link_id: input.linkId,
    p_title: input.title,
    p_image_url: input.imageUrl ?? null,
    p_price_cents: input.priceCents ?? null,
    p_is_visible: input.isVisible ?? true,
  });

  if (error) throw error;
  if (!data) throw new Error("storefront_item_upsert_failed");

  // A RPC retorna a linha de storefront_items sem o join de links.
  // marketplaceCode/destinationUrl chegam vazios aqui; o refetch da query de
  // itens (via invalidacao no onSuccess) repopula com o join completo.
  const row = data as {
    id: string;
    link_id: string;
    title: string;
    image_url: string | null;
    price_cents: number | null;
    position: number;
    is_visible: boolean;
    marketplace_code?: string;
    destination_url?: string;
  };

  return {
    id: row.id,
    linkId: row.link_id,
    marketplaceCode: row.marketplace_code ?? "",
    destinationUrl: row.destination_url ?? "",
    title: row.title,
    imageUrl: row.image_url,
    priceCents: row.price_cents,
    position: row.position,
    isVisible: row.is_visible,
  };
}

/** Insere/edita um item da vitrine (valida link do tenant no DB). */
export function useUpsertStorefrontItem() {
  const queryClient = useQueryClient();

  return useMutation<StorefrontItem, Error, UpsertStorefrontItemInput>({
    mutationFn: callUpsertStorefrontItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STOREFRONT_ITEMS_QUERY_KEY });
    },
  });
}

async function callDeleteStorefrontItem(itemId: string): Promise<void> {
  const { error } = await supabase.from("storefront_items").delete().eq("id", itemId);
  if (error) throw error;
}

/** Remove um item da vitrine (RLS garante isolamento por tenant). */
export function useDeleteStorefrontItem() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: callDeleteStorefrontItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STOREFRONT_ITEMS_QUERY_KEY });
    },
  });
}

async function callReorderStorefrontItems(itemIds: string[]): Promise<void> {
  const { error } = await supabase.rpc("reorder_storefront_items", {
    p_item_ids: itemIds,
  });

  if (error) throw error;
}

/** Persiste a nova ordem de itens (array de ids na ordem desejada). */
export function useReorderStorefrontItems() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string[]>({
    mutationFn: callReorderStorefrontItems,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STOREFRONT_ITEMS_QUERY_KEY });
    },
  });
}

/** Traduz codigos de erro das RPCs para mensagens amigaveis em pt-BR. */
export function storefrontErrorLabel(message: string): string {
  const labels: Record<string, string> = {
    pro_plan_required:
      "Publicar a vitrine exige o plano Pro. Faça o upgrade para liberar a página pública.",
    slug_empty: "Informe um endereço (slug) para a sua vitrine.",
    slug_invalid: "O endereço da vitrine tem caracteres inválidos. Use letras, números e hífens.",
    slug_reserved: "Esse endereço é reservado pelo sistema. Escolha outro.",
    slug_taken: "Esse endereço já está em uso por outra loja. Escolha outro.",
    storefront_status_invalid: "Status da vitrine inválido.",
    branding_invalid: "Os dados de personalização da vitrine estão inválidos.",
    branding_layout_invalid: "Layout da vitrine inválido.",
    branding_accent_invalid: "A cor de destaque precisa estar em formato hexadecimal, ex: #1F8A4C.",
    tenant_not_found: "Conta não encontrada. Recarregue a página e tente de novo.",
    title_required: "Informe um título para o item da vitrine.",
    image_url_invalid: "A URL da imagem precisa começar com http ou https.",
    price_cents_invalid: "O preço informado é inválido.",
    link_required: "Selecione um link para adicionar à vitrine.",
    link_not_owned: "Esse link não pertence à sua conta.",
    item_not_found: "Item da vitrine não encontrado.",
  };

  const known = Object.entries(labels).find(([code]) => message.includes(code));
  return known?.[1] ?? message;
}
